import React from "react";
import { Check, ChevronsUpDown, X as XIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

/**
 * Combobox searchable de partidas del baremo asignado al expediente.
 * Al seleccionar una entrada, llama a onSelect(item) con {code, descripcion, precio, p_minimo, p_maximo, gremio, unidad}
 * y a onClear() si el perito elige "Sin baremo · escribir libre".
 * Props:
 *  - items: lista cruda del backend (/api/siniestros/{id}/baremo-items)
 *  - currentCode, currentDesc: para mostrar el chip actual
 *  - sinBaremo: bool, marca el modo "estimación libre"
 *  - testid: prefijo de data-testid
 */
export function BaremoCombobox({ items, currentCode, currentDesc, baremoLabel, sinBaremo, onSelect, onClear, testid = "partida-baremo" }) {
  const [open, setOpen] = React.useState(false);
  const list = Array.isArray(items) ? items : [];

  // Etiqueta del trigger: si hay baremoLabel (descripción del baremo elegido,
  // estable y no editable por el usuario) usamos esa. Si no, caemos al texto
  // libre (compatibilidad con expedientes antiguos sin baremo_descripcion).
  const stableLabel = (baremoLabel || currentDesc || "").trim();
  const label = sinBaremo
    ? "Sin baremo"
    : (currentCode && stableLabel)
      ? `${currentCode} · ${stableLabel.slice(0, 32)}`
      : (stableLabel ? stableLabel.slice(0, 40) : "Elegir partida del baremo…");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full text-left text-[12px] px-2 py-1 rounded bg-transparent border border-transparent hover:bg-[#FBFAF6] hover:border-[#E5E2D8] focus:bg-[#FBFAF6] focus:border-[#D85A30] outline-none flex items-center gap-1 ${sinBaremo ? "text-[#9A988F] italic" : "text-[#2C2C2A]"}`}
          data-testid={`${testid}-trigger`}
        >
          <span className="flex-1 truncate">{label}</span>
          <ChevronsUpDown className="h-3 w-3 text-[#7A786F] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command shouldFilter>
          <CommandInput placeholder="Buscar por código, descripción o gremio…" data-testid={`${testid}-input`} />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>Sin coincidencias en el baremo.</CommandEmpty>
            <CommandGroup heading={`Baremo (${list.length} partidas)`}>
              {list.map((it, idx) => {
                const value = `${it.code || ""} ${it.descripcion} ${it.gremio || ""}`.trim().toLowerCase();
                const selected = !sinBaremo && (it.code === currentCode);
                return (
                  <CommandItem
                    key={`${it.code || "_"}_${idx}`}
                    value={value}
                    onSelect={() => {
                      onSelect?.(it);
                      setOpen(false);
                    }}
                    className="text-[12px]"
                    data-testid={`${testid}-item-${idx}`}
                  >
                    <div className="flex items-start gap-2 w-full min-w-0">
                      {selected ? <Check className="h-3.5 w-3.5 text-[#1D9E75] mt-0.5" /> : <span className="w-3.5" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {it.code && <span className="font-mono text-[#1D9E75] text-[11px]">{it.code}</span>}
                          <span className="text-[#2C2C2A] truncate">{it.descripcion}</span>
                        </div>
                        <div className="text-[10px] text-[#7A786F] flex gap-2">
                          {it.gremio && <span>{it.gremio}</span>}
                          {it.unidad && <span>· {it.unidad}</span>}
                          <span className="ml-auto font-mono">{Number(it.precio || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                        </div>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandGroup heading="Otras opciones">
              <CommandItem
                value="sin baremo escribir libre"
                onSelect={() => { onClear?.(); setOpen(false); }}
                className="text-[12px] text-[#9A988F] italic"
                data-testid={`${testid}-clear`}
              >
                <XIcon className="h-3.5 w-3.5 mr-2" /> Sin baremo · escribir libre
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
